import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { MembersAPI, getApiBaseUrl } from '../utils/api';
import { getToken } from '../utils/auth';
import { FaUserTimes, FaBan, FaCheck, FaUser, FaSearch } from 'react-icons/fa';
import ConfirmModal from './ConfirmModal';
import SpinnerLoading from './SpinnerLoading';

interface Member {
    id: string;
    name: string;
    email: string;
    avatar?: string | null;
    role: string;
    access_level: string;
    status: 'active' | 'banned';
    joined_at: string;
}

interface MemberManagementProps {
    classId: string;
}

const MemberManagement: React.FC<MemberManagementProps> = ({ classId }) => {
    const [members, setMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'active' | 'banned'>('active');
    const [searchTerm, setSearchTerm] = useState('');

    const getAvatarUrl = (path?: string | null) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;

        // Fix stored path being /api/public/... => /public/...
        const cleanPath = path.replace('/api/public', '/public');

        // Get base URL (remove /index.php/api or /api)
        // http://127.0.0.1/quiz-backend-php/index.php/api => http://127.0.0.1/quiz-backend-php
        const baseUrl = getApiBaseUrl().split('/index.php')[0].split('/api')[0];

        // Ensure strictly one slash between base and path? 
        // cleanPath starts with /, baseUrl might not end with /
        return `${baseUrl}${cleanPath}`;
    };

    // Confirm Modal State
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        title: string;
        message: React.ReactNode;
        type: 'danger' | 'info' | 'success';
        onConfirm: () => void;
        confirmText?: string;
        isLoading?: boolean;
    }>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
        onConfirm: () => { }
    });

    const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

    const fetchMembers = async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token) return;
            const data = await MembersAPI.list(classId, token);
            setMembers(data.members as Member[]);
        } catch (error: any) {
            toast.error(error.message || 'Không thể tải thành viên');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMembers();
    }, [classId]);

    const handleActionRequest = (memberId: string, action: 'kick' | 'ban' | 'unban', memberName: string) => {
        let title = '';
        let message: React.ReactNode = '';
        let type: 'danger' | 'info' | 'success' = 'danger';
        let confirmText = 'Xác nhận';

        switch (action) {
            case 'kick':
                title = 'Mời thành viên ra khỏi lớp';
                message = <span>Bạn có chắc chắn muốn mời <b>{memberName}</b> ra khỏi lớp không?</span>;
                confirmText = 'Mời ra';
                break;
            case 'ban':
                title = 'Cấm thành viên';
                message = <span>Bạn có chắc chắn muốn cấm <b>{memberName}</b> tham gia lớp học và làm bài kiểm tra không?</span>;
                confirmText = 'Cấm ngay';
                break;
            case 'unban':
                title = 'Gỡ lệnh cấm';
                message = <span>Bạn có chắc chắn muốn bỏ cấm cho <b>{memberName}</b> không?</span>;
                type = 'success';
                confirmText = 'Bỏ cấm';
                break;
        }

        setConfirmModal({
            isOpen: true,
            title,
            message,
            type,
            confirmText,
            onConfirm: () => executeAction(memberId, action)
        });
    };

    const executeAction = async (memberId: string, action: 'kick' | 'ban' | 'unban') => {
        try {
            setConfirmModal(prev => ({ ...prev, isLoading: true }));
            const token = getToken();
            if (!token) return;

            await MembersAPI.manage(classId, memberId, action, token);
            toast.success('Thao tác thành công');

            // Close modal then refresh
            closeConfirmModal();
            fetchMembers();
        } catch (error: any) {
            toast.error(error.message || 'Có lỗi xảy ra');
            setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
    };

    const filteredMembers = members.filter(m => {
        const matchesTab = activeTab === 'active' ? m.status === 'active' : m.status === 'banned';
        const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.email.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesTab && matchesSearch;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div style={{ transform: 'scale(0.435)' }}>
                    <SpinnerLoading />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex bg-gray-100 dark:bg-gray-700 p-1 rounded-xl">
                    <button
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'active'
                            ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-300 shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                            }`}
                        onClick={() => setActiveTab('active')}
                    >
                        Thành viên ({members.filter(m => m.status === 'active').length})
                    </button>
                    <button
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'banned'
                            ? 'bg-white dark:bg-gray-600 text-red-600 dark:text-red-300 shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                            }`}
                        onClick={() => setActiveTab('banned')}
                    >
                        Đã chặn ({members.filter(m => m.status === 'banned').length})
                    </button>
                </div>

                <div className="relative w-full sm:w-64">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaSearch className="text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Tìm kiếm thành viên..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                </div>
            </div>

            {/* List */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {filteredMembers.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                            <p>Không tìm thấy thành viên nào.</p>
                        </div>
                    ) : (
                        filteredMembers.map(member => (
                            <div key={member.id} className="group flex flex-col sm:flex-row items-center justify-between p-4 hover:bg-white dark:hover:bg-gray-700/50 transition-colors gap-4">
                                <div className="flex items-center gap-4 w-full sm:w-auto">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg shadow-sm overflow-hidden ${!member.avatar ? (member.access_level === 'owner'
                                        ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                                        : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400') : ''
                                        }`}>
                                        {member.avatar ? (
                                            <img
                                                src={getAvatarUrl(member.avatar) || ''}
                                                alt={member.name}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    e.currentTarget.parentElement?.classList.add('bg-gray-200', 'text-gray-500', 'dark:bg-gray-700', 'dark:text-gray-400');
                                                }}
                                            />
                                        ) : (
                                            <FaUser />
                                        )}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                                                {member.name}
                                            </h3>
                                            {member.access_level === 'owner' && (
                                                <span className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded-full">
                                                    Owner
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[200px] sm:max-w-md">
                                            {member.email}
                                        </p>

                                    </div>
                                </div>

                                <div className="flex gap-2 w-full sm:w-auto justify-end">
                                    {member.access_level !== 'owner' && (
                                        activeTab === 'active' ? (
                                            <>
                                                <button
                                                    onClick={() => handleActionRequest(member.id, 'kick', member.name)}
                                                    className="px-3 py-1.5 text-sm font-medium text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20 rounded-lg transition-colors flex items-center gap-1.5"
                                                    title="Mời ra khỏi lớp"
                                                >
                                                    <FaUserTimes /> <span className="hidden sm:inline">Mời ra</span>
                                                </button>
                                                <button
                                                    onClick={() => handleActionRequest(member.id, 'ban', member.name)}
                                                    className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center gap-1.5"
                                                    title="Cấm tham gia"
                                                >
                                                    <FaBan /> <span className="hidden sm:inline">Cấm</span>
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                onClick={() => handleActionRequest(member.id, 'unban', member.name)}
                                                className="px-3 py-1.5 text-sm font-medium text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded-lg transition-colors flex items-center gap-1.5"
                                                title="Gỡ lệnh cấm"
                                            >
                                                <FaCheck /> <span className="hidden sm:inline">Bỏ cấm</span>
                                            </button>
                                        )
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={closeConfirmModal}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.confirmText}
                isLoading={confirmModal.isLoading}
            />
        </div >
    );
};

export default MemberManagement;
